/**
 * DataRoomWelcomePage — public landing page a collaborator lands on
 * after clicking the accept-invite link in their email.
 *
 * The backend at `/api/data-room/invite/:token`:
 *   1) flips the collaborator row to invite_accepted=true
 *   2) fires an admin notification email
 *   3) 302 redirects here with query params: biotech, email, role, first
 *
 * We keep this page intentionally minimal + login-neutral: the invited
 * user may or may not already have a ForesightRx session, so we surface
 * a clear "Sign in" CTA that carries them into the actual room.
 */
import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, ArrowRight, Loader2, ShieldCheck } from 'lucide-react';
import MagicLinkLogin from '@/components/MagicLinkLogin';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ROLE_LABEL = {
  admin:  'Administrator',
  owner:  'Company Owner',
  editor: 'Editor',
  viewer: 'Viewer',
};

export default function DataRoomWelcomePage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const biotechId = params.get('biotech') || '';
  const biotechNameParam = params.get('biotech_name') || '';
  const email = params.get('email') || '';
  const role = (params.get('role') || 'viewer').toLowerCase();
  const isFirst = params.get('first') === '1';

  const [biotechName, setBiotechName] = useState(biotechNameParam);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [isAuthed, setIsAuthed] = useState(false);

  // Fetch biotech name only if the redirect didn't include it (defensive
  // fallback for older invite links). Uses the public status endpoint
  // which is unauthenticated.
  useEffect(() => {
    if (biotechName || !biotechId) return;
    let cancel = false;
    axios
      .get(`${API}/biotech/${biotechId}`, { withCredentials: true })
      .then(res => { if (!cancel) setBiotechName(res.data?.company_name || ''); })
      .catch(() => { /* not authed / no public endpoint — fine, "the data room" */ });
    return () => { cancel = true; };
  }, [biotechId, biotechName]);

  // Silently check whether the user already has a session — if yes,
  // we can pop them straight into the CRM data room view.
  useEffect(() => {
    let cancel = false;
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then(res => { if (!cancel) setIsAuthed(!!res.data?.email); })
      .catch(() => { /* not signed in — show sign-in CTA */ })
      .finally(() => { if (!cancel) setCheckingAuth(false); });
    return () => { cancel = true; };
  }, []);

  const targetHref = biotechId ? `/biotech/${biotechId}` : '/dashboard';
  const roleLabel = ROLE_LABEL[role] || 'Collaborator';

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-4 py-10" data-testid="data-room-welcome-page">
      <div className="w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-sm p-8 space-y-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <div className="text-[11px] font-semibold tracking-wider text-slate-500 uppercase">ForesightRx · Data Room</div>
            <div className="text-lg font-bold text-slate-900">
              {isFirst ? "You're in." : "Welcome back."}
            </div>
          </div>
        </div>

        <p className="text-sm leading-relaxed text-slate-700" data-testid="dr-welcome-message">
          {email ? <><span className="font-semibold text-slate-900">{email}</span> has been granted </> : 'You have been granted '}
          <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded border border-indigo-200 bg-indigo-50 text-indigo-700 uppercase tracking-wide">
            <ShieldCheck className="w-3 h-3" />{roleLabel}
          </span>{' '}
          access to the <span className="font-semibold text-slate-900">{biotechName || 'data room'}</span>.
        </p>

        {isFirst && (
          <div className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-[12px] text-emerald-800">
            Your inviter has been notified that you&apos;ve joined. All file previews and downloads are logged in the room&apos;s activity feed.
          </div>
        )}

        {/* Sign-in area — depends on session state */}
        <div className="pt-2">
          {checkingAuth ? (
            <div className="inline-flex items-center gap-2 text-sm text-slate-500">
              <Loader2 className="w-4 h-4 animate-spin" /> Preparing your view…
            </div>
          ) : isAuthed ? (
            <button
              type="button"
              onClick={() => navigate(targetHref)}
              className="inline-flex items-center gap-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 shadow-sm"
              data-testid="dr-welcome-open-btn"
            >
              Open the data room
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <div className="space-y-4">
              <div>
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500 mb-2">Sign in to continue</div>
                <MagicLinkLogin
                  defaultEmail={email}
                  testIdPrefix="dr-welcome-magic"
                  onSuccess={() => {
                    // Session cookie is set — hand off to the room.
                    window.location.href = targetHref;
                  }}
                />
              </div>
              <div className="text-[11px] text-slate-400 text-center">
                Prefer Google?{' '}
                <a href="/login" className="text-indigo-600 hover:text-indigo-800 font-medium">
                  Sign in with Google →
                </a>
              </div>
            </div>
          )}
        </div>

        <p className="text-[11px] text-slate-400 border-t border-slate-100 pt-4">
          Trouble accessing? Reply to the invite email or contact ForesightRx BD.
        </p>
      </div>
    </div>
  );
}
